import React, { useState } from "react";
import { withFormik, Form, Field } from "formik";
import * as Yup from "yup";
import { FormGroup, Label, Button, Col, Row, Card, Input } from "reactstrap";
import { toast } from "react-toastify";
import axios from "../../services/api/api";
import { PostSupervisorCompany } from "../../actions/teacherMutations";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { GetSupervisorCompanies } from "../../actions/teacherQueries";
import Pacmanloader from "../../components/Pacmanloader/Pacmanloader";
import { FormattedMessage, useIntl } from "react-intl";
import PromptAddSupervisorToSchool from "../Modal/PromptAddSupervisorToSchool";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";
import { compareStrings } from "../../Utilities/sortHelper";

const CreateNewSupervisor = ({
  values,
  errors,
  touched,
  isSubmitting,
  dismissModal,
  context,
  supervisorToAddToSchool,
  toggleAddToSchool,
  addToSchoolIsOpen,
  schoolId,
  setSubmitting,
}) => {
  const intl = useIntl();
  const [addNewCompany, setAddNewCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");

  const postSupervisorCompanyMutation = useMutation({ mutationFn: PostSupervisorCompany });
  const companies = GetSupervisorCompanies();
  const queryClient = useQueryClient();

  if (companies.isPending) {
    return <Pacmanloader />;
  }
  const companiesList = () => {
    let tempList = [];
    companies.data.forEach((c) => {
      let companyToAdd = { value: c, label: c.name };
      tempList.push(companyToAdd);
    });
    let addNewCompany = {
      value: { id: "Add" },
      label: intl.formatMessage({ id: "AddNewCompany" }),
    };
    // Sortera före unshift så att "Lägg till nytt företag" ligger kvar överst.
    tempList.sort((a, b) => compareStrings(a.label, b.label));
    tempList.unshift(addNewCompany);
    return tempList;
  };

  const handleSetCompany = (val) => {
    if (val === "Add") {
      setAddNewCompany(true);
    } else {
      values.supervisorCompanyId = val;
    }
  };

  const handleSubmitNewCompany = () => {
    let cleanedCompanyName = newCompanyName.replace("&", "%26");
    postSupervisorCompanyMutation.mutate(cleanedCompanyName, {
      onSuccess: (data) => {
        if (data && data.status > 199 && data.status < 300) {
          toast.success(intl.formatMessage({ id: "CompanyAdded" }));
          queryClient.invalidateQueries({ queryKey: ["FetchSupervisorCompanies"] });
        } else {
          toast.warn(data);
        }
        setAddNewCompany(false);
      },
      throwOnError: true,
      onError: (data) => {
        toast.warn(data.response.data);
      },
    });
  };

  return (
    <Card className="mb-0">
      {context ? (
        <div className="px-3 py-3 border-bottom">
          <div style={{ fontWeight: 600, fontSize: 15 }}>
            {context.type === "single" ? (
              <FormattedMessage
                id="AddSupervisorForStudent"
                values={{ name: context.studentName }}
              />
            ) : (
              <FormattedMessage
                id="AddSupervisorForNStudents"
                values={{ count: context.count }}
              />
            )}
          </div>
        </div>
      ) : (
        ""
      )}
      <Form className="px-3 pt-3">
        <Row>
          <Col lg={6}>
            <FormGroup>
              <Label>
                <FormattedMessage id="Name" />
              </Label>
              <Field
                className={`form-control ${
                  errors.supervisorName &&
                  touched.supervisorName &&
                  "is-invalid"
                }`}
                type="input"
                name="supervisorName"
                placeholder={intl.formatMessage({ id: "Name" })}
                defaultValue={values.supervisorName}
              />
              {errors.supervisorName && touched.supervisorName && (
                <div className="invalid-feedback">{errors.supervisorName}</div>
              )}
            </FormGroup>
          </Col>
          <Col lg={6}>
            <FormGroup>
              <Label>
                <FormattedMessage id="Email" />
              </Label>
              <Field
                className={`form-control ${
                  errors.supervisorEmail &&
                  touched.supervisorEmail &&
                  "is-invalid"
                }`}
                type="input"
                name="supervisorEmail"
                placeholder={intl.formatMessage({ id: "Email" })}
                defaultValue={values.supervisorEmail}
              />
              {errors.supervisorEmail && touched.supervisorEmail && (
                <div className="invalid-feedback">{errors.supervisorEmail}</div>
              )}
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col lg={6}>
            <FormGroup>
              <Label>
                <FormattedMessage id="Phone" />{" "}
              </Label>
              <Field
                className={`form-control ${
                  errors.supervisorPhone &&
                  touched.supervisorPhone &&
                  "is-invalid"
                }`}
                type="input"
                name="supervisorPhone"
                placeholder={intl.formatMessage({ id: "Phone" })}
                defaultValue={values.supervisorPhone}
              />
              {errors.supervisorPhone && touched.supervisorPhone && (
                <div className="invalid-feedback">{errors.supervisorPhone}</div>
              )}
            </FormGroup>
          </Col>
          <Col lg={6}>
            {!addNewCompany ? (
              <FormGroup>
                <Label for="supervisorCompany">
                  <FormattedMessage id="Company" />
                </Label>
                <Field
                  component="select"
                  name="supervisorCompanyId"
                  id="supervisorCompany"
                  onChange={(val) => handleSetCompany(val.target.value)}
                  value={companiesList().find(
                    (c) => c.value.id === values.supervisorCompanyId
                  )}
                  className={`form-control ${
                    errors.supervisorCompanyId &&
                    touched.supervisorCompanyId &&
                    "is-invalid"
                  }`}
                >
                  <option value="-1" key="-1">
                    {intl.formatMessage({ id: "ChooseCompanyFromList" })}
                  </option>
                  {companiesList().map((c, i) => {
                    return (
                      <option key={c.value.id} value={c.value.id}>
                        {c.label}
                      </option>
                    );
                  })}
                </Field>
                {errors.supervisorCompanyId && touched.supervisorCompanyId && (
                  <div className="invalid-feedback">
                    {errors.supervisorCompanyId}
                  </div>
                )}
              </FormGroup>
            ) : (
              <>
                <Label for="CreatedCompanyName">
                  <FormattedMessage id="AddCompany" />
                </Label>
                <Row className="m-0 p-0">
                  <Input
                    type="text"
                    value={newCompanyName}
                    onChange={(data) => setNewCompanyName(data.target.value)}
                  />
                </Row>
                <Row className="m-0 p-0">
                  <Col lg={6} className="p-0">
                    <Button
                      outline
                      className="p-1 mt-1"
                      color="secondary"
                      onClick={() => setAddNewCompany(false)}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Col>
                  <Col lg={6} className="p-0">
                    <Button
                      onClick={() => handleSubmitNewCompany()}
                      outline
                      className="p-1 mt-1"
                      color="primary"
                    >
                      <FormattedMessage id="Add" />
                    </Button>
                  </Col>
                </Row>
              </>
            )}
          </Col>
        </Row>

        <div className="d-flex justify-content-end gap-2 mt-3 mx-n3 mb-0 px-3 py-3 border-top">
          <Button
            outline
            color="secondary"
            type="button"
            onClick={dismissModal}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button
            outline
            color="primary"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <FormattedMessage id="Wait" />
            ) : context ? (
              <FormattedMessage id="AddAndAssign" />
            ) : (
              <FormattedMessage id="AddPersons" />
            )}
          </Button>
        </div>
      </Form>
      <PromptAddSupervisorToSchool
        supervisor={supervisorToAddToSchool}
        toggle={toggleAddToSchool}
        isOpen={addToSchoolIsOpen}
        dismissModal={dismissModal}
        schoolId={schoolId}
        setSubmitting={setSubmitting}
      />
    </Card>
  );
};

const FormikForm = withFormik({
  mapPropsToValues: (props) => ({
    supervisorName: props.supervisorName,
    supervisorEmail: props.supervisorEmail,
    supervisorPhone: props.supervisorPhone,
    supervisorCompanyId: props.supervisorCompanyId,
  }),
  validationSchema: Yup.object().shape({
    supervisorName: Yup.string().required(<FormattedMessage id="Required" />),
    supervisorEmail: Yup.string()
      .email(<FormattedMessage id="MustBeValidEmail" />)
      .max(255)
      .required(<FormattedMessage id="EmailRequired" />),
    supervisorCompanyId: Yup.number()
      .typeError(<FormattedMessage id="SupervisorNeedsCompany" />)
      .required(<FormattedMessage id="SupervisorNeedsCompany" />)
      .positive(<FormattedMessage id="SupervisorNeedsCompany" />),
  }),

  handleSubmit(values, { props, resetForm, setSubmitting }) {
    let supervisor = {
      Name: values.supervisorName,
      Email: values.supervisorEmail,
      Phone: values.supervisorPhone,
      CompanyId: values.supervisorCompanyId,
      VisibleForSchoolId: props.schoolId,
    };
    axios
      .post(`/Apl/PostSupervisorForSchool`, supervisor)
      .then((res) => {
        if (res.data.result.createdUser) {
          props.onSupervisorCreated(res.data.result.createdUser);
          axios
            .post(
              `/User/SendActivationMail?email=${encodeURIComponent(
                supervisor.Email
              )}`
            )
            .then(() => {
              toast.success("Handledare har skapats", TOAST_CONFIG);
              resetForm();
            })
            .catch((err) => {
              toast.warn(<FormattedMessage id="NewActivationMailFailed" />);
            });
          props.dismissModal();
        } else if (res.data.result.alreadyExistingUser) {
          if (
            res.data.result.alreadyExistingUser.userTypeOf !== "AplSupervisor"
          ) {
            toast.warn(<FormattedMessage id="UserExistsAndIsNotSupervisor" />);
          } else {
            props.setSupervisorToAddToSchool(
              res.data.result.alreadyExistingUser
            );
            props.setAddToSchoolIsOpen(true);
          }
        }
      })
      .catch((err) => {
        toast.error(
          err.response ? err.response.data : err.message,
          TOAST_CONFIG
        );
        props.dismissModal();
      });
    props.handleReloadSupervisors();
    setSubmitting(false);
  },
})(CreateNewSupervisor);

export default FormikForm;
